import { apiPost, apiPut } from './client';

// ============================================
// Request Types
// ============================================

export interface StartAccountRobotRequest {
  email: string;
  params?: Record<string, unknown>;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CreateAccountRequest {
  reimbursementRef: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface VerifyForgotPasswordRequest {
  email: string;
  code: string;
}

export interface ChangePasswordRequest {
  email: string;
  token: string;
  password: string;
}

// ============================================
// Response Types
// ============================================

export interface StartAccountRobotResponse {
  ref: string;
  email?: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface CreateAccountResponse {
  accessToken: string;
  refreshToken: string;
}

export interface VerifyForgotPasswordResponse {
  token: string;
}

// ============================================
// Error Response Type
// ============================================

export interface ApiErrorResponse {
  errorCode: string;
  message?: string;
}

// ============================================
// API Functions
// ============================================

/**
 * Start a new reimbursement flow
 * Creates a new ref for the reimbursement process
 * @throws ApiErrorResponse with errorCode: REIMBURSEMENT_ACCOUNT_LOGIN_REQUIRED if account exists
 */
export const startAccountRobot = async (
  data: StartAccountRobotRequest
): Promise<StartAccountRobotResponse> => {
  return apiPost<StartAccountRobotResponse>('api/v1/reimbursements', data);
};

/**
 * Login with email and password
 * @throws ApiErrorResponse with errorCode: REIMBURSEMENT_LOGIN_WRONG_CREDENTIALS or REIMBURSEMENT_ACCOUNT_NOT_FOUND
 */
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  return apiPost<LoginResponse>('api/v1/reimbursement-accounts/login', data);
};

/**
 * Create a new account after quiz completion
 * Sets password for the reimbursement account
 */
export const createAccount = async (
  data: CreateAccountRequest
): Promise<CreateAccountResponse> => {
  return apiPost<CreateAccountResponse>('api/v1/reimbursement-accounts', data);
};

/**
 * Refresh the access token using refresh token
 */
export const refreshToken = async (token: string): Promise<LoginResponse> => {
  return apiPost<LoginResponse>('api/v1/reimbursement-accounts/refresh-token', {
    refreshToken: token,
  });
};

/**
 * Request password reset email
 * Sends 6-digit verification code to email
 */
export const submitForgotPassword = async (
  data: ForgotPasswordRequest
): Promise<void> => {
  return apiPost<void>('api/v1/reimbursement-accounts/forgot-password', data);
};

/**
 * Verify forgot password code
 * Returns token needed for password change
 * @throws ApiErrorResponse with errorCode: REIMBURSEMENT_ACCOUNT_FORGOT_PASSWORD_INVALID_CODE
 */
export const verifyForgotPassword = async (
  data: VerifyForgotPasswordRequest
): Promise<VerifyForgotPasswordResponse> => {
  return apiPost<VerifyForgotPasswordResponse>(
    'api/v1/reimbursement-accounts/forgot-password/verify',
    data
  );
};

/**
 * Change password after verification
 * Requires email, token (from verifyForgotPassword), and new password
 */
export const changePassword = async (
  data: ChangePasswordRequest
): Promise<void> => {
  return apiPut<void>('api/v1/reimbursement-accounts/forgot-password', data);
};

/**
 * Logout - clear tokens from localStorage
 */
export const logout = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  sessionStorage.clear();
};
